/**
 * TemplatesStep — Quick-start duel templates shown before the full wizard.
 */
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { SPACE, CARD, FONT, TRACKING, SECTION_LABEL } from '@/constants/designTokens';
import type { DuelType, Exercise } from './types';

export interface DuelTemplate {
  emoji: string;
  title: string;
  description: string;
  /** null means "open full wizard" */
  prefill: {
    duelType: DuelType;
    exercise?: { id: string; name: string; category: string };
    duration: number;
  } | null;
}

const TEMPLATES: DuelTemplate[] = [
  {
    emoji: '🏋️',
    title: 'Heaviest Bench This Week',
    description: 'Who can bench the most in 7 days?',
    prefill: {
      duelType: 'strength',
      exercise: { id: 'bench-press', name: 'Bench Press', category: 'chest' },
      duration: 168,
    },
  },
  {
    emoji: '💪',
    title: 'Most Workouts This Week',
    description: 'Pure consistency — who shows up more?',
    prefill: {
      duelType: 'consistency',
      duration: 168,
    },
  },
  {
    emoji: '🔥',
    title: 'Heaviest Squat This Week',
    description: 'Legs don\'t lie. Heaviest squat wins.',
    prefill: {
      duelType: 'strength',
      exercise: { id: 'back-squat', name: 'Back Squat', category: 'legs' },
      duration: 168,
    },
  },
  {
    emoji: '⚡',
    title: 'Custom Challenge',
    description: 'Build your own duel from scratch.',
    prefill: null,
  },
];

interface Props {
  colors: any;
  onSelectTemplate: (template: DuelTemplate) => void;
}

export default function TemplatesStep({ colors: c, onSelectTemplate }: Props) {
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={[styles.sectionLabel, { color: c.accent }]}>QUICK START</Text>
      <Text style={[styles.heading, { color: c.text }]}>PICK A DUEL</Text>
      <Text style={[styles.subtitle, { color: c.textSecondary ?? c.text }]}>
        Choose a template or build your own.
      </Text>

      {TEMPLATES.map((tpl, i) => (
        <TouchableOpacity
          key={i}
          style={[
            styles.card,
            {
              backgroundColor: c.card ?? c.surface,
              borderColor: tpl.prefill === null ? c.accent : c.border,
              borderWidth: tpl.prefill === null ? 2 : 1,
            },
          ]}
          onPress={() => onSelectTemplate(tpl)}
          activeOpacity={0.7}
        >
          <Text style={styles.emoji}>{tpl.emoji}</Text>
          <View style={styles.cardText}>
            <Text style={[styles.cardTitle, { color: c.text }]}>{tpl.title}</Text>
            <Text style={[styles.cardDesc, { color: c.textSecondary ?? c.text }]}>
              {tpl.description}
            </Text>
          </View>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: SPACE.lg },
  sectionLabel: {
    ...SECTION_LABEL,
    marginBottom: SPACE.xs,
  },
  heading: {
    fontSize: FONT['2xl'],
    fontFamily: 'Inter_700Bold',
    letterSpacing: TRACKING.wide,
    textTransform: 'uppercase',
    marginBottom: SPACE.sm,
  },
  subtitle: {
    fontSize: FONT.base,
    fontFamily: 'Inter_400Regular',
    marginBottom: SPACE.lg,
  },
  card: {
    ...CARD.standard,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SPACE.base,
  },
  emoji: {
    fontSize: 32,
    marginRight: SPACE.md,
  },
  cardText: {
    flex: 1,
  },
  cardTitle: {
    fontSize: FONT.md,
    fontFamily: 'Inter_600SemiBold',
    letterSpacing: TRACKING.tight,
    marginBottom: 2,
  },
  cardDesc: {
    fontSize: FONT.sm,
    fontFamily: 'Inter_400Regular',
  },
});
